// Wire codes of the binders and sealed product API (mtg-api
// docs/superpowers/specs/2026-10-18-binders-sealed-api.md). Every literal
// here is a machine code the server sends or accepts, or a CSS value, never
// text a user reads; the copy for each lives in the `collection` catalog
// under binders.* and sealed.*.

/** The binder color palette (the server's CHECK constraint), in shelf order. */
export const BINDER_COLORS = ['forest', 'ocean', 'oxblood', 'mustard', 'plum', 'slate', 'black', 'canvas'] as const
export type BinderColor = typeof BINDER_COLORS[number]

/** The spine fill of each palette key (approved mockup). */
export const BINDER_HEX: Record<BinderColor, string> = {
  forest: '#2f5a3a',
  ocean: '#2f5d8a',
  oxblood: '#7a2e2e',
  mustard: '#8a6a1f',
  plum: '#4b2f6b',
  slate: '#3d3d46',
  black: '#1f1f24',
  canvas: '#b0a58a',
}

/** Spine text on a light binder (canvas) is dark; everything else light. */
export const LIGHT_BINDERS: readonly BinderColor[] = ['canvas']

/** The Unsorted spine (a plain neutral), not a palette key. */
export const UNSORTED_HEX = '#4a4a52'

/**
 * `deck` (deck boxes): a ManaBox deck box, created by an import. Never a move
 * target, never created or edited by the binder editor.
 */
export const BINDER_KIND = { manual: 'manual', smart: 'smart', unsorted: 'unsorted', deck: 'deck' } as const
export type BinderKind = typeof BINDER_KIND[keyof typeof BINDER_KIND]

/** The decoded value of a wire enum member this client does not know (a newer server). */
export const UNKNOWN = 'unknown' as const

/** A deck box's runner state (`outbox_state`); 'unknown' for a value this client does not know. */
export const DECK_OUTBOX_STATES = ['pending', 'pending_replace', 'done'] as const
export type DeckOutboxState = typeof DECK_OUTBOX_STATES[number] | 'unknown'

/** A deck box's flags: the last commit kept the user's edits, or the row changed again. */
export const DECK_BOX_FLAGS = ['kept_yours', 'changed_again'] as const
export type DeckBoxFlag = typeof DECK_BOX_FLAGS[number] | 'unknown'

/**
 * Every smart rule field the API takes, in ITS canonical order
 * (mtg-api collection_binders.RULE_FIELDS).
 *
 * `copies` (real copies of the card across every printing, an integer
 * 1..10,000, gte / lte) and `printing_copies` (the same count, but of the
 * line's own printing + finish) are not gated: every client offers them, as
 * one "Copies" rule whose This printing / All printings control picks the
 * field (COPIES_SCOPE_FIELD). `deck` (in / not_in a deck box, value
 * ["boxes"]) is offered only when the list's `limits.deck` is present
 * (DECK_GATED_RULE_FIELDS); with the gate off a saved one rides along
 * untouched, exactly like a field this client does not know.
 *
 * `spare` (the card's REAL copies minus the copies its My Decks need, summed
 * across decks and clamped at 0; an integer 0..10,000, gte / lte) is offered
 * only when the list's `limits.spare` is present (SPARE_GATED_RULE_FIELDS);
 * with that gate off a saved one rides along untouched.
 */
export const RULE_FIELDS = [
  'name', 'set', 'color', 'type', 'rarity', 'finish', 'condition', 'status', 'language', 'mv', 'price', 'added',
  'copies', 'printing_copies', 'deck', 'spare',
] as const
export type RuleField = typeof RULE_FIELDS[number]

/** The fields the rule builder offers and edits for everyone (the rest are kept as saved). */
export const EDITABLE_RULE_FIELDS = [
  'name', 'set', 'color', 'type', 'rarity', 'finish', 'condition', 'status', 'language', 'mv', 'price', 'added',
  'copies', 'printing_copies',
] as const satisfies readonly RuleField[]

/**
 * The fields the builder offers and edits only when `limits.deck` is
 * present (the API's parse_rules refuses `deck` with the gate off).
 */
export const DECK_GATED_RULE_FIELDS = ['deck'] as const satisfies readonly RuleField[]

/**
 * The fields the builder offers and edits only when `limits.spare` is
 * present (the API's parse_rules refuses `spare` with its gate off).
 */
export const SPARE_GATED_RULE_FIELDS = ['spare'] as const satisfies readonly RuleField[]

/** The deck rule's ops: "In a deck" / "Not in a deck". */
export const DECK_RULE_OPS = ['in', 'not_in'] as const

export const RULE_OP = {
  in: 'in', any: 'any', fits: 'fits', exact: 'exact', gte: 'gte', lte: 'lte', contains: 'contains', notIn: 'not_in',
} as const
export type RuleOp = typeof RULE_OP[keyof typeof RULE_OP]

/** Color rule modes; "exact" is the builder's default. */
export const RULE_COLOR_MODES = ['exact', 'any', 'fits'] as const

/** Range fields: at most one gte and one lte each. */
export const RANGE_FIELDS = ['price', 'added', 'mv', 'copies', 'printing_copies', 'spare'] as const

/** The deck rule's only value (collection_binders.DECK_RULE_VALUE). */
export const DECK_RULE_VALUE = ['boxes'] as const
/** The copies rules' bounds (inclusive), for copies and printing_copies alike. */
export const COPIES_RULE_MIN = 1
export const COPIES_RULE_MAX = 10_000
/**
 * The spare rule's bounds (inclusive). 0 is allowed: spare is clamped at 0,
 * so `spare <= 0` matches the cards your decks use every copy of (or more).
 */
export const SPARE_RULE_MIN = 0
export const SPARE_RULE_MAX = 10_000

/**
 * What a Copies rule counts, the editor's This printing / All printings
 * control, in its order. Each maps to its own API field: "printing" is
 * `printing_copies` (the REAL copies of the line's own printing + finish),
 * "card" is `copies` (across every printing of the card).
 */
export const COPIES_SCOPES = ['printing', 'card'] as const
export type CopiesScope = typeof COPIES_SCOPES[number]
export const COPIES_SCOPE_FIELD = {
  printing: 'printing_copies', card: 'copies',
} as const satisfies Record<CopiesScope, RuleField>
export type CopiesField = typeof COPIES_SCOPE_FIELD[CopiesScope]
/** The two copies fields (one "Copies" rule in the builder). */
export const COPIES_FIELDS: readonly CopiesField[] = COPIES_SCOPES.map(s => COPIES_SCOPE_FIELD[s])
/** A NEW Copies rule counts the line's own printing. */
export const COPIES_SCOPE_NEW: CopiesScope = 'printing'

/**
 * The "Trade binder" preset's conditions (status owned or trade, 2 or more
 * copies of the same printing, not in a deck), in the API's stored form. The
 * deck condition is dropped while the deck gate is off.
 */
export const TRADE_PRESET_CONDITIONS = [
  { field: 'status', op: 'in', value: ['owned', 'trade'] },
  { field: 'printing_copies', op: 'gte', value: 2 },
  { field: 'deck', op: 'not_in', value: [...DECK_RULE_VALUE] },
] as const satisfies readonly { field: RuleField; op: string; value: unknown }[]

/**
 * The "Trade binder" preset with the spare gate on: status owned or trade,
 * 2 or more copies of the card (across printings), and at least 1 copy your
 * My Decks do not use. The deck-box condition is kept only with the deck
 * gate on, as above.
 */
export const TRADE_PRESET_SPARE_CONDITIONS = [
  { field: 'status', op: 'in', value: ['owned', 'trade'] },
  { field: 'copies', op: 'gte', value: 2 },
  { field: 'deck', op: 'not_in', value: [...DECK_RULE_VALUE] },
  { field: 'spare', op: 'gte', value: 1 },
] as const satisfies readonly { field: RuleField; op: string; value: unknown }[]

export const MATCH_ALL = 'all' as const
/** The rule preview waits this long after the last edit (the server allows 10/min). */
export const PREVIEW_DEBOUNCE_MS = 500
export const MAX_RULE_CONDITIONS = 12
/** The API's per-rule limits (collection_binders.py). */
export const MAX_RULE_VALUES = 100
export const NAME_RULE_MAX = 200
export const MV_RULE_MAX = 100
/** Per-value character limits of the list fields (the search body's). */
export const RULE_VALUE_MAX_LEN: Record<string, number> = {
  set: 16, type: 16, rarity: 16, finish: 64, condition: 64, status: 64, language: 16, color: 1,
}
export const BINDER_NAME_MAX = 60
/**
 * The per-collection binder limits when the list response carries none
 * (`GET /binders` sends `limits: {manual, smart}`; an older server did not).
 */
export const BINDER_LIMITS_DEFAULT = { manual: 200, smart: 20 } as const
// (`limits.deck` and `limits.spare` have no default: ABSENT means that gate is off.)

/** `detail` codes of binder writes, mapped to inline messages. */
export const BINDER_ERROR = {
  duplicateCondition: 'duplicate_condition',
  emptyRange: 'empty_range',
  nameTaken: 'binder_name_taken',
  nameReserved: 'binder_name_reserved',
  binderLimit: 'binder_limit',
  smartLimit: 'smart_binder_limit',
  matchAny: 'match_any_unsupported',
  unknownPrinting: 'unknown_printing',
  revMismatch: 'binders_rev_mismatch',
  qtyCap: 'qty_cap',
  notManual: 'binder_not_manual',
  /** parse_rules refused the rules (an unknown field, op or value), `field` when it names one. */
  invalid: 'invalid',
} as const

/** Client-side name checks (nameIssue). */
export const NAME_ISSUE = { empty: 'empty', tooLong: 'too_long', reserved: 'reserved', taken: 'taken' } as const
export type NameIssue = typeof NAME_ISSUE[keyof typeof NAME_ISSUE]

/** Client-side rule checks (buildRules); duplicate_condition and empty_range are the server's own codes. */
export const RULE_ISSUE = {
  empty: 'empty', duplicate: 'duplicate_condition', emptyRange: 'empty_range', badPrice: 'bad_price',
  badDate: 'bad_date', colorlessMix: 'colorless_mix', badValue: 'bad_value', badMv: 'bad_mv', tooMany: 'too_many_values',
  badCopies: 'bad_copies',
  badSpare: 'bad_spare',
} as const
export type RuleIssue = typeof RULE_ISSUE[keyof typeof RULE_ISSUE]

/** Where a binder write error shows in the editor. */
export const ERROR_AT = { name: 'name', rules: 'rules', cover: 'cover', form: 'form' } as const
export type ErrorAt = typeof ERROR_AT[keyof typeof ERROR_AT]

/** The colorless color code (alone only, for an exact rule). */
export const COLORLESS = 'C'

/** A typed price that is not a valid amount. */
export const PRICE_INVALID = 'invalid'

/** Query keys of GET /sealed/catalog. */
export const CATALOG_QUERY = { q: 'q', set: 'set' } as const

/** Names the server reserves (case-insensitive), checked before sending. */
export const RESERVED_BINDER_NAMES = ['unsorted', 'collection', 'trade', 'proxies', 'wants'] as const

export const SHELF_VIEWS = ['shelf', 'stack'] as const
export type ShelfView = typeof SHELF_VIEWS[number]

export const BINDER_VIEWS = ['pages', 'list'] as const
export type BinderView = typeof BINDER_VIEWS[number]

/** A binder page is a 3x3 grid of pockets by default. */
export const POCKETS_PER_PAGE = 9

/** The pocket grids a binder page can show (pockets per row = rows per page), picked per device. */
export const BINDER_GRID_SIZES = [3, 4, 5] as const
export type BinderGridSize = typeof BINDER_GRID_SIZES[number]
export const DEFAULT_BINDER_GRID: BinderGridSize = 3

/** The binder settings menu's edit entries (each opens the editor). */
export const BINDER_MENU = ['rename', 'color', 'cover'] as const
/**
 * The settings menu's Share entry (binder share, Phase 1): manual and smart
 * binders only, and only while the list says `share_enabled`. It opens the
 * share sheet, not the editor, so it is not one of BINDER_MENU's edit entries.
 */
export const BINDER_MENU_SHARE = 'share' as const

/** The collection screen's sections (per-copy only). */
export const SECTIONS = ['cards', 'binders', 'sealed'] as const
export type Section = typeof SECTIONS[number]

/** localStorage keys. */
export const BINDER_STORAGE = {
  shelfView: 'cb_binders_shelf_view',
  binderView: 'cb_binder_view',
  /** The binder pockets' grouping (By printing, By card name, Each copy): the user's choice beats the size default. */
  binderGroup: 'cb_binder_group',
  /** The Pages view's pocket grid (3, 4 or 5 a side). */
  binderGrid: 'cb_binder_grid',
} as const

// --- Sealed ---------------------------------------------------------------------

/** The free-text category vocabulary (anything else is a 422). */
export const SEALED_CATEGORIES = [
  'booster_box', 'booster_pack', 'bundle', 'deck', 'box_set', 'prerelease_kit', 'secret_lair', 'other',
] as const
export type SealedCategory = typeof SEALED_CATEGORIES[number]

export const SEALED_STATUSES = ['owned', 'want'] as const
export type SealedStatus = typeof SEALED_STATUSES[number]

/** The box glyph of a category (catalog categories are wider than the free-text vocabulary). */
export const BOX_SHAPE = { wide: 'wide', medium: 'medium', tall: 'tall', collector: 'collector', pack: 'pack' } as const
export type BoxShape = typeof BOX_SHAPE[keyof typeof BOX_SHAPE]

export const SEALED_ERROR = {
  insufficientQty: 'insufficient_qty',
  qtyMismatch: 'qty_mismatch',
  cannotOpenWant: 'cannot_open_want',
  rowCap: 'sealed_row_cap',
  unknownProduct: 'unknown_product',
  qtyCap: 'qty_cap',
  invalid: 'invalid',
  catalogUnavailable: 'sealed_catalog_unavailable',
} as const

export const SEALED_QTY_MAX = 1000
export const SEALED_NAME_MAX = 120
export const SEALED_PRICE_MAX = 100000

/** The redirect's `src` query value for this client. */
export const BUY_SRC = 'web'
